import { Injectable } from '@angular/core';
import { EDataBindingMode } from '@models/layout/enums';
import { HardcodedData, IDataSourceItem, IVirtualPanelItem } from '@se/cp.common';
import { DataBinding, Guid } from '@se/se.ui.html5';
import { BehaviorSubject, filter, Observable, of, Subscription } from 'rxjs';
import { isNotNullOrUndefined } from '../cp-configuration/utils/utils';
import { CpEntityList, CpUndoEntity, ICpEntity, isEntity } from '../cp-configuration/view-model';
import { CpDatasourceService } from '../cp-datasource.service';
import { CpModelService } from '../cp-model.service';
import { CpFlatNode } from '../cp-node';
import { CpNodeService } from '../cp-node.service';
import {
    countVirtualChildByParentId,
    createUniqueKey,
    decrementVisibleMenuItems,
    filterVirtualPanelForAdvancedItem,
    getEntityIndex,
    getSelectedSourceItemId,
    incrementVisibleMenuItems,
    isOnRequestDataBindingMode,
    isPropertyExclusiveDataBindingMode,
    isPropertyOrPropertyExclusiveDataBindingMode,
    isSingleDataBindingMode,
    setMenuItemVisibility,
} from './routines';
import {
    AddedNodeId,
    Binding,
    IMenuItem,
    ITabSelector,
    ParentNodeId,
    UniqueKey,
    VirtualEntityInfo,
    VirtualPanelId,
} from './types';

@Injectable({
    providedIn: 'root',
})
export class TabSelectorService {
    nodeService?: CpNodeService;
    tabSelectors: Record<ParentNodeId, ITabSelector> = {};

    /** strutture dati per il funzionamento del tabSelector */
    private virtualPanelsByBinding: Record<Binding, IVirtualPanelItem[]> = {};

    private modelSubscribersByBinding: Record<Binding, Subscription> = {};

    private parentNodeIdByVirtualPanelId: Record<VirtualPanelId, ParentNodeId> = {};

    private dataBindingsToRegister: DataBinding[] = []; //servono per registrare ciascun data binding al model service. non si puo fare all'inizio perche non esisterebbero ancora in corrispondenti nodi nello store e nel node service

    private setOfAddedVirtualPanelsUniqueKey = new Set<UniqueKey>();

    private addedNodesInfo: Record<AddedNodeId, VirtualEntityInfo> = {};

    private addedPanelsByEntityBinding: Record<Binding, Record<Binding, AddedNodeId[]>> = {};

    get allNodeIds() {
        return Object.values(this.addedPanelsByEntityBinding)
            .map((record) => Object.values(record))
            .flat(2);
    }

    constructor(
        private modelService: CpModelService,
        private dataSourceService: CpDatasourceService,
    ) {}

    /** inizializzazione del servizio */
    init(nodeService: CpNodeService) {
        this.nodeService = nodeService;
        this.startListeners();

        /*task 88222 solo se il referto e` refertato
         * Report.ReferenceExamination.IsReported*/
        this.initMultipleDefaultPanels();
    }
    restart() {
        this.clearForRestart();
        this.startListeners();

        /*task 88222 solo se il referto e` refertato
         * Report.ReferenceExamination.IsReported*/
        this.initMultipleDefaultPanels();
    }

    private startListeners = () => {
        this.dataBindingsToRegister.forEach((binding) => this.registerModel(binding));
    };

    initMultipleDefaultPanels() {
        const isReported = this.modelService.getValue(new DataBinding('Report.ReferenceExamination.IsReported'));
        if (isReported) {
            return;
        }

        /*task 88222 se multiple aggiungere tante schede figlie quanto parametro defaultMultiplePanelsNumber*/
        Object.values(this.tabSelectors).forEach((tabSelector) => {
            const multiplePanelMenuItems = tabSelector.menuItems.filter(
                (item) =>
                    item.virtualPanel?.virtualPanel.dataBindingMode === EDataBindingMode.Multiple &&
                    item.virtualPanel?.virtualPanel?.defaultMultiplePanelsNumber,
            );

            multiplePanelMenuItems.forEach((menuItem) => {
                if (!menuItem.virtualPanel) {
                    return;
                }
                let defaultMultiplePanelsNumber = menuItem.virtualPanel.virtualPanel.defaultMultiplePanelsNumber || 0;
                /*bug 89142 conteggiare e sottrarre le item gia presenti*/
                const dataBinding = menuItem.virtualPanel.virtualPanel.dataBinding ?? '';
                const entity = this.modelService.getEntity(new DataBinding(dataBinding));
                const entities = entity?.toJSON() || [];
                defaultMultiplePanelsNumber -= Array.isArray(entities) ? entities.length : 0;
                for (let i = 0; i < defaultMultiplePanelsNumber; i++) {
                    this.add(menuItem);
                }
            });
        });
    }

    /**
     * registra i listeners su i singoli binding e ascolta i cambiamenti del model
     * @param dataBinding
     * @returns
     */
    private registerModel(dataBinding: DataBinding) {
        const binding = dataBinding.toString();
        if (!!this.modelSubscribersByBinding[binding]) return;
        this.modelSubscribersByBinding[binding] = this.modelService
            .registerEntity(dataBinding, false)
            .pipe(filter(isNotNullOrUndefined))
            .subscribe((parentEntity) => {
                if (parentEntity instanceof CpUndoEntity) {
                    this.handleModelSingleRemove(parentEntity);
                } else {
                    this.handleModelListAdd(parentEntity);
                    this.handleModelListRemove(parentEntity);
                }
            });
    }

    /**
     * rimuove un pannello nel caso di binding mode single
     * @param entity
     */
    private handleModelSingleRemove = (entity: CpUndoEntity) => {
        const binding = entity.path?.toStringSimple() ?? '';
        const nodeAdded = this.addedPanelsByEntityBinding[binding];
        /** CpUndoEntity perde l'informazione dell'id -> si devono cancellare tutti i nodi che fanno riferimento al binding */
        const nodeIdsToRemove = Object.values(nodeAdded).flat();
        this.removePanelNodes(nodeIdsToRemove, true);
    };

    /**
     * rimuove un pannello nel caso di lista ovvero tutti i binding mode diversi da single
     * @param parentEntity
     */
    private handleModelListRemove = (parentEntity: ICpEntity) => {
        const children = parentEntity.getAllEntities(new DataBinding());
        const parentBinding = parentEntity.path?.toStringSimple() ?? '';
        const setChildrenPath = new Set(children?.map((entity) => entity.path?.toString()));
        const nodeIdsToRemove: string[] = !!this.addedPanelsByEntityBinding[parentBinding]
            ? Object.entries(this.addedPanelsByEntityBinding[parentBinding])
                  .filter(([binding, _]) => !setChildrenPath.has(binding))
                  .map(([_, nodeIds]) => nodeIds)
                  .flat()
            : [];

        this.removePanelNodes(nodeIdsToRemove);
    };

    /**
     * handler che ascolta le modifiche sul modelService, si preoccupa di aggiungere pannelli
     * e di modificare le strutture dati interna al tab selector service
     * @param parentEntity
     */
    handleModelListAdd = (parentEntity: ICpEntity) => {
        //se non si tratta di binding avanzato bisogna prendere l'ultimo pezzo del binding
        const children = parentEntity.getAllEntities(new DataBinding());
        children
            ?.filter((entity): entity is ICpEntity => !!entity)
            .forEach((entity) => {
                const childBinding = entity.path?.toStringSimple() ?? '';
                const virtualPanels = this.virtualPanelsByBinding[childBinding];

                const virtualPanelsToAdd = virtualPanels
                    /** filtrare solo per binding avanzato. se non è avanzato  non filtrare*/
                    .filter((virtualPanel) => filterVirtualPanelForAdvancedItem(virtualPanel, entity, parentEntity))
                    /** filtra i pannelli da aggiungere quelli non già renderizzati */
                    .filter((virtualPanel) => {
                        const parentNodeId = this.parentNodeIdByVirtualPanelId[virtualPanel.id];
                        const selectedSourceItemId = getSelectedSourceItemId(virtualPanel, entity, parentEntity);
                        const uniqueKey = createUniqueKey(
                            virtualPanel,
                            parentNodeId,
                            entity.path?.toString() ?? '',
                            selectedSourceItemId ?? '',
                        );
                        return !this.setOfAddedVirtualPanelsUniqueKey.has(uniqueKey);
                    });
                virtualPanelsToAdd.forEach((virtualPanel) => {
                    if (!this.nodeService) {
                        return;
                    }
                    //pannelli da aggiungere
                    const parentNodeId = this.parentNodeIdByVirtualPanelId[virtualPanel.id];
                    const selectedSourceItemId = getSelectedSourceItemId(virtualPanel, entity, parentEntity) ?? '';
                    const index = getEntityIndex(entity);
                    const addedNode = this.addPanelNode(parentNodeId, virtualPanel, entity, parentEntity, index);
                    if (addedNode) {
                        this.updateSelectorInfoOnAdd(parentNodeId, virtualPanel, selectedSourceItemId);

                        const controlBindingList = this.nodeService
                            .getAllControlByNode(addedNode.id)
                            .map((control) => Reflect.get(control, 'dataBinding')?.toString() ?? null)
                            .filter(isNotNullOrUndefined);

                        this.addedNodesInfo[addedNode.id] = {
                            node: addedNode,
                            virtualPanel,
                            parentNodeId,
                            selectedSourceItemId: selectedSourceItemId ? String(selectedSourceItemId) : undefined,
                            tooltip: virtualPanel.virtualPanel.removeButtonTooltip || '',
                            entityPath: entity.path,
                            parentPath: entity.parent?.path,
                            controlBindingList,
                        };
                        const entityPath = entity.path?.toString() ?? '';
                        const uniqueKey = createUniqueKey(
                            virtualPanel,
                            parentNodeId,
                            entityPath,
                            selectedSourceItemId ?? '',
                        );

                        this.setOfAddedVirtualPanelsUniqueKey.add(uniqueKey);

                        const parentBinding = parentEntity.path?.toStringSimple() ?? '';
                        if (!this.addedPanelsByEntityBinding[parentBinding])
                            this.addedPanelsByEntityBinding[parentBinding] = {};
                        if (!this.addedPanelsByEntityBinding[parentBinding][entityPath])
                            this.addedPanelsByEntityBinding[parentBinding][entityPath] = [];
                        this.addedPanelsByEntityBinding[parentBinding][entityPath].push(addedNode.id);
                    }
                });
            });
    };

    /**
     * crea struttura dati virtualPanelsByBinding record di supporto
     * @param dataBinding
     * @param virtualPanel
     * @param parentNodeId
     */
    private addVirtualPanelInMap = (
        dataBinding: DataBinding,
        virtualPanel: IVirtualPanelItem,
        parentNodeId: string,
    ) => {
        const isPropOrExclusive = isPropertyOrPropertyExclusiveDataBindingMode(virtualPanel);

        const binding = isPropOrExclusive ? dataBinding.entity.toString() : dataBinding.toString();

        const list = this.virtualPanelsByBinding[binding] || [];
        list.push(virtualPanel);
        this.virtualPanelsByBinding[binding] = list;
        this.parentNodeIdByVirtualPanelId[virtualPanel.id] = parentNodeId;
    };

    /**
     * crea i tab selector e i menu collegati a partire dal singolo virtual panel
     * @param parentNodeId
     * @param virtualPanel
     * @returns
     */
    addTabSelector = (parentNodeId: ParentNodeId, virtualPanel: IVirtualPanelItem): void => {
        /*per ora onRequest saltiamo*/
        if (isOnRequestDataBindingMode(virtualPanel)) return;
        const dataBinding = new DataBinding(new DataBinding(virtualPanel.virtualPanel.dataBinding).simple);

        this.addVirtualPanelInMap(dataBinding, virtualPanel, parentNodeId);

        const isPropOrExclusive = isPropertyOrPropertyExclusiveDataBindingMode(virtualPanel);
        const bindingToAdd = isPropOrExclusive ? dataBinding.entity : dataBinding;

        if (!this.dataBindingsToRegister.find((db) => db.equals(bindingToAdd))) {
            this.dataBindingsToRegister.push(bindingToAdd);
        }

        // se ho già un tab selector per il padre attuale -> estiste già un pannello virtuale assegnato a tutti gli item. chiamo nuova funzione per assegnare il nuovo pannello virtuale ad l'item corrispondente
        if (!!this.tabSelectors[parentNodeId]) return this.addVirtualPanelToTabSelector(parentNodeId, virtualPanel);

        // ciclo tutti gli item e assegno a tutti il primo pannello virtuale trovato
        const menuItems: IMenuItem[] = this.initMenuItems(virtualPanel, parentNodeId);
        // creo il nuovo selector per il pannello padre
        this.tabSelectors[parentNodeId] = {
            id: new Guid().toString(),
            parentNodeId,
            dataBinding,
            menuItems,
            visibleMenuItemsCount$: new BehaviorSubject(0), //all'inizio tutti visibili
        };
        //aggiungo il virtual panel se non è property databinding mode
        this.addVirtualPanelToTabSelector(parentNodeId, virtualPanel);
    };

    private initMenuItems = (virtualPanel: IVirtualPanelItem, parentNodeId: string): IMenuItem[] => {
        if (isPropertyOrPropertyExclusiveDataBindingMode(virtualPanel))
            return (
                this.dataSourceService.getById(virtualPanel.virtualPanel.idDataSource)?.map((item) => ({
                    id: item.Id,
                    parentNodeId,
                    // valore iniziale che dipende dalle source e come lunghezza non cambia. cambieranno solo i valori hidden per mostrare o no le items nel template
                    hidden$: new BehaviorSubject(true),
                    itemSource: item,
                    tooltip: '',
                    virtualPanel: null,
                })) ?? []
            );
        return [
            {
                id: '',
                parentNodeId,
                hidden$: new BehaviorSubject(true),
                itemSource: undefined,
                tooltip: '',
                virtualPanel: null,
            },
        ];
    };

    /**
     *
     * aggiunge un virtual panel alla struttura dati del tabSelector esistente
     * @param parentNodeId
     * @param virtualPanel
     */
    private addVirtualPanelToTabSelector = (parentNodeId: ParentNodeId, virtualPanel: IVirtualPanelItem): void => {
        /** aggiungere al singolo menuItem la toolbar del suo virtual panel. se c'è */
        /** oppure aggiungere direttamente tutto il virtual panel all'interno del singolo menuItem */
        const advancedSelectedItemId: string = new DataBinding(
            virtualPanel.virtualPanel.dataBinding,
        ).getFirstAdvancedItemId(); // prendere id del virtuale @123 che potrebbe non esistere
        /** cerco il menuItem inserisco il pannello virtuale a lui collegato */
        if (!!advancedSelectedItemId) {
            const menuItem = this.tabSelectors[parentNodeId].menuItems.find(
                (item) => item.id === advancedSelectedItemId,
            );
            if (menuItem) {
                menuItem.virtualPanel = virtualPanel;
                menuItem.tooltip = virtualPanel.virtualPanel.addButtonTooltip ?? '';
                menuItem.hidden$.next(false);
                incrementVisibleMenuItems(this.tabSelectors[parentNodeId]);
            }
        } else {
            this.tabSelectors[parentNodeId].menuItems.forEach((menuItem) => {
                menuItem.virtualPanel = virtualPanel;
                menuItem.tooltip = virtualPanel.virtualPanel.addButtonTooltip ?? '';
                menuItem.hidden$.next(false);
                incrementVisibleMenuItems(this.tabSelectors[parentNodeId]);
            });
        }
    };

    /**
     * chiamato al click sul button di rimozione nel navigator node
     * rimuove la entity dal model di conseguenza la routine che ascolta tale modifica si preoccupera di aggioranre il rollup
     * @param nodeId
     */
    remove(nodeId: string) {
        const nodeInfo = this.addedNodesInfo[nodeId];
        if (!nodeInfo) return;
        const { entityPath, controlBindingList } = nodeInfo;
        const entity = !!entityPath ? this.modelService.getEntity(entityPath) : null;
        if (!isEntity(entity)) return;
        controlBindingList.forEach((binding) => {
            const virtualBinding = entity.path?.toString() ?? '';
            if (binding.includes(virtualBinding)) return;
            const controlBinding = new DataBinding(binding);
            const controlValue = this.modelService.getValue(new DataBinding(binding));
            if (isEntity(controlValue)) this.removeEntityProperty(controlBinding);
            if (controlBinding.isAdvanced()) return this.removeAdvancedBindingProperty(controlBinding.entity);
            else this.removeSimpleProperty(controlBinding);
        });
        this.removeEntity(entity);
    }

    canRemove(nodeId: string): boolean {
        const nodeInfo = this.addedNodesInfo[nodeId];
        if (!nodeInfo) return false;
        const { virtualPanel, parentNodeId } = nodeInfo;
        const defaultMultiplePanelsNumber = virtualPanel.virtualPanel.defaultMultiplePanelsNumber || 0;
        const childrenCount = countVirtualChildByParentId(parentNodeId, this.setOfAddedVirtualPanelsUniqueKey);
        return childrenCount > defaultMultiplePanelsNumber;
    }

    removeSimpleProperty(controlBinding: DataBinding) {
        const property = controlBinding.property.toString();
        const parentEntity = this.modelService.getEntity(controlBinding.entity);
        const indexedParentBinding = parentEntity?.path;
        if (!indexedParentBinding) return;
        const bindingToSet = indexedParentBinding.addSimple(property);
        this.modelService.setValue(bindingToSet, null, { emitEvent: true });
    }

    removeAdvancedBindingProperty(objectBinding: DataBinding) {
        /** comportamento speciale per le misure */
        if (objectBinding.toStringSimple() === HardcodedData.bindings.ecoCv.measures.toStringSimple()) {
            this.removeMeasureProperty(objectBinding);
        } else {
            /** elimino tutti gli altri oggetti avanzati. */
            const entity = this.modelService.getValue(objectBinding);
            if (!isEntity(entity)) return;
            this.removeEntity(entity);
        }
    }
    removeEntityProperty(controlBinding: DataBinding) {
        const entity = this.modelService.getValue(controlBinding);
        if (!isEntity(entity)) return;
        this.removeEntity(entity);
    }
    removeMeasureProperty(measureBinding: DataBinding) {
        const measureEntity = this.modelService.getValue(measureBinding);
        if (!isEntity(measureEntity)) return;
        const measureDetails = measureEntity.getPropertyValue('Detail');
        let canRemove: boolean = true;
        if (measureDetails instanceof CpEntityList) {
            const detailsList = measureDetails.activeEntities;
            detailsList.forEach((detail) => {
                const isManualModified: boolean = !!detail.getPropertyValue('IsManualModified');
                if (isManualModified) measureEntity.remove(detail);
            });
            if (!!measureDetails.activeEntities.length) canRemove = false;
        }
        if (canRemove) this.removeEntity(measureEntity);
    }
    /**
     * chiamato al click sul menu di aggiunta di un nodo virtuale
     * aggiunge la corrispondente entity nel model service
     * l'handler che ascolta si preoccupa di aggiungere il nodo corrispondente
     * @param menuItem
     */
    add(menuItem: IMenuItem) {
        if (!menuItem.virtualPanel) return;
        const binding = menuItem.virtualPanel.virtualPanel.dataBinding;
        this.addEntity(binding, menuItem.itemSource);
    }

    /**
     * aggiunge un dato reale nel model service
     * @param binding
     * @param itemSource
     * @private
     */
    private addEntity(binding: string, itemSource?: IDataSourceItem) {
        //@TODO rimuovere undoentity altrimenti l'add si blocca sul caso single
        this.modelService.createEntityForVirtualPanel(binding, itemSource);
    }

    /**
     * rimuove dato reale dal model service
     * @param entity
     * @private
     */
    private removeEntity(entity: ICpEntity) {
        this.modelService.removeEntity(entity);
    }

    /**
     * aggiorna i dati del tab selector relativo ad un pannello virtuale
     * quando viene aggiunto un nodo. modifica quindi la visibilita ed il conteggio dei nodi
     * @param parentNodeId
     * @param virtualPanel
     * @param selectedSourceItemId
     */
    updateSelectorInfoOnAdd = (
        parentNodeId: ParentNodeId, //id del pannello dinamico che contiene il pannello virtuale
        virtualPanel: IVirtualPanelItem,
        selectedSourceItemId: string,
    ) => {
        //aggiornare informazioni nel tab selector info
        const tabSelectorInfo = this.tabSelectors[parentNodeId];
        const isPropExclusive = isPropertyExclusiveDataBindingMode(virtualPanel);
        const isSingle = isSingleDataBindingMode(virtualPanel);

        if (!isSingle && !isPropExclusive) return;

        const menuItem = isPropExclusive
            ? tabSelectorInfo.menuItems.find((item) => item.id === selectedSourceItemId)
            : tabSelectorInfo.menuItems[0];

        if (menuItem) {
            setMenuItemVisibility(menuItem, true);
            decrementVisibleMenuItems(tabSelectorInfo);
        }
    };

    /**
     * aggiorna i dati del tab selector relativo ad un pannello virtuale in fase di rimozione nodo
     * modificha visibilita e conteggio dei nodi
     * @param nodeInfo
     */
    updateSelectorInfoOnRemove = (nodeInfo: VirtualEntityInfo) => {
        const { virtualPanel, parentNodeId, selectedSourceItemId } = nodeInfo;

        const tabSelectorInfo = this.tabSelectors[parentNodeId];
        const isPropExclusive = isPropertyExclusiveDataBindingMode(virtualPanel);
        const isSingle = isSingleDataBindingMode(virtualPanel);

        if (!isSingle && !isPropExclusive) return;

        const menuItem = isPropExclusive
            ? tabSelectorInfo.menuItems.find((item) => item.id === selectedSourceItemId)
            : tabSelectorInfo.menuItems[0];

        if (menuItem) {
            setMenuItemVisibility(menuItem, false);
            incrementVisibleMenuItems(tabSelectorInfo);
        }
    };

    /**
     *
     * @param parentNodeId
     * @param virtualPanel
     * @param entity
     * @param parent
     * @param index
     * @private
     */
    private addPanelNode(
        parentNodeId: ParentNodeId, //id del pannello dinamico che contiene il pannello virtuale
        virtualPanel: IVirtualPanelItem,
        entity: ICpEntity,
        parent: ICpEntity,
        index: number,
    ): CpFlatNode | undefined {
        const actualDataBinding =
            parent instanceof CpEntityList ? parent.path.addIndex(parent?.getEntityIndex(entity) ?? 0) : parent.path;
        const isIndexToAdd = index > 0 && Boolean(virtualPanel.virtualPanel.showIndex);
        const header: string = isIndexToAdd ? `${virtualPanel.header} ${index + 1}` : virtualPanel.header;
        const panel: IVirtualPanelItem = { ...virtualPanel, header };
        return this.nodeService?.hydrateVirtualNode(
            parentNodeId,
            panel, // virtual panel da renderizzare
            entity.Id?.toString() || '', // id della entity
            actualDataBinding,
        );
    }

    /**
     * rimuove pannello nella view
     * @param nodeIds
     * @param forceRemoveAll
     * @private
     */
    private removePanelNodes(nodeIds: string[], forceRemoveAll: boolean = false) {
        this.nodeService?.removeNodesById(...nodeIds);
        nodeIds.forEach((nodeId) => {
            const nodeInfo = this.addedNodesInfo[nodeId];
            const { virtualPanel, entityPath, parentPath, parentNodeId } = nodeInfo;
            const parentBinding = parentPath?.toStringSimple();

            if (parentBinding) {
                if (forceRemoveAll) {
                    this.addedPanelsByEntityBinding[parentBinding] = {};
                } else {
                    const entityBinding = entityPath?.toString() ?? '';
                    this.addedPanelsByEntityBinding[parentBinding][entityBinding] = this.addedPanelsByEntityBinding[
                        parentBinding
                    ][entityBinding].filter((nid) => nid !== nodeId);
                }
            }

            this.updateSelectorInfoOnRemove(nodeInfo);
            const uniqueKey = createUniqueKey(
                virtualPanel,
                parentNodeId,
                entityPath?.toString() ?? '',
                nodeInfo.selectedSourceItemId ?? '',
            );
            this.setOfAddedVirtualPanelsUniqueKey.delete(uniqueKey);
        });
    }

    getNodeVirtualInfo(id: string) {
        return this.addedNodesInfo[id] || null;
    }

    getTabSelectorsFromParentId = (parentNodeId: ParentNodeId): ITabSelector | null =>
        this.tabSelectors[parentNodeId] ?? null;

    getMenuItemsFromParentId = (parentNodeId: ParentNodeId): IMenuItem[] =>
        this.getTabSelectorsFromParentId(parentNodeId)?.menuItems || [];

    getVisibleMenuItemsCountFromParentId = (parentNodeId: ParentNodeId): Observable<number> =>
        this.getTabSelectorsFromParentId(parentNodeId)?.visibleMenuItemsCount$ || of(0);

    safeExit = () => {
        this.tabSelectors = {};
        this.virtualPanelsByBinding = {};
        Object.values(this.modelSubscribersByBinding).forEach((subscriber) => subscriber.unsubscribe());
        this.modelSubscribersByBinding = {};
        this.parentNodeIdByVirtualPanelId = {};
        this.dataBindingsToRegister = [];
        this.setOfAddedVirtualPanelsUniqueKey.clear();
        this.addedNodesInfo = {};
        this.addedPanelsByEntityBinding = {};
    };
    clear = this.safeExit;

    clearForRestart = () => {
        this.removePanelNodes(this.allNodeIds, true);
    };
}
